import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader } from "@/components/common";
import { formatINR } from "@/lib/format";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, ComposedChart } from "recharts";
import { LineChart as LineIcon, Info, Package, Wallet } from "lucide-react";

export default function Forecast() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/forecast").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader label="Building forecast…" />;

  const combined = [
    ...data.history.map((h) => ({ month: h.month, sales: h.sales })),
    ...data.projection.map((p) => ({ month: p.month, projected: p.projected_sales, low: p.low, high: p.high })),
  ];

  return (
    <div>
      <PageHeader title="Business Forecast" subtitle="Where you're heading — based on your trends." />

      <div className="mb-6 rounded-xl border border-primary/25 bg-primary/5 p-4 flex gap-3 animate-fade-up" data-testid="forecast-note">
        <Info className="h-5 w-5 text-primary shrink-0" />
        <p className="text-sm text-foreground/80">{data.note}</p>
      </div>

      <SectionCard title="Sales Trajectory" subtitle="6 months history + 3 months projection" className="mb-6" testid="forecast-chart">
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={combined} margin={{ left: -18, right: 6 }}>
            <defs>
              <linearGradient id="fRange" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.2} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => formatINR(v, { compact: true })} />
            <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))" }} />
            <Area dataKey="high" stroke="none" fill="url(#fRange)" />
            <Line type="monotone" dataKey="sales" name="Actual" stroke="#6366F1" strokeWidth={3} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="projected" name="Projected" stroke="#8B5CF6" strokeWidth={3} strokeDasharray="6 4" dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </SectionCard>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        {data.projection.map((p, i) => (
          <SectionCard key={i} testid={`forecast-month-${i}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{p.month} — estimate</div>
            <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{formatINR(p.projected_sales, { compact: true })}</div>
            <div className="text-xs text-muted-foreground mt-1">Range {formatINR(p.low, { compact: true })}–{formatINR(p.high, { compact: true })}</div>
            <div className="mt-3 flex justify-between text-sm border-t border-border pt-3">
              <span className="text-muted-foreground">Est. profit</span>
              <span className="font-semibold text-[hsl(var(--success))]">{formatINR(p.projected_profit, { compact: true })}</span>
            </div>
          </SectionCard>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <SectionCard testid="forecast-inventory">
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-11 w-11 rounded-lg bg-primary/10 text-primary"><Package className="h-5 w-5" /></div>
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Suggested Inventory Buy</div><div className="font-heading text-xl font-bold">{formatINR(data.inventory_need, { compact: true })}</div></div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">Estimated stock investment to meet next month's demand based on recent velocity.</p>
        </SectionCard>
        <SectionCard testid="forecast-cash">
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-11 w-11 rounded-lg bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"><Wallet className="h-5 w-5" /></div>
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Expected Cash Requirement</div><div className="font-heading text-xl font-bold">{formatINR(data.cash_need, { compact: true })}</div></div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">Approximate cash needed to cover upcoming fixed expenses. Keep a buffer.</p>
        </SectionCard>
      </div>
    </div>
  );
}
